/* What the provider screen decides, as pure functions — testable with no DOM.
 *
 * **The form is driven by the catalog the server serves.** Which fields a provider has, which are
 * sensitive, and which authentication method shows which field are facts about the backend; a
 * screen restating them would be a second answer to "what does Bedrock need" that drifts the first
 * time a field is added. So this file filters the catalog and never lists a provider's fields itself.
 */

import type {
  AuthMethod,
  ConnectionStatus,
  ModelConfigView,
  ProviderCategory,
  ProviderConfigView,
  ProviderField,
  ProviderKind,
  ProviderSpec,
} from '@/shared/api/generated/ai-serving';

export const CATEGORY_ORDER: readonly ProviderCategory[] = [
  'direct',
  'enterprise',
  'custom',
];

export type Tone = 'ok' | 'warn' | 'error' | 'unknown';

/** A total map, so a twelfth status is a compile error rather than a badge with no colour. */
const STATUS_TONE = {
  untested: 'unknown',
  connected: 'ok',
  connection_failed: 'error',
  invalid_credentials: 'error',
  model_not_found: 'error',
  deployment_not_found: 'error',
  access_denied: 'error',
  rate_limited: 'warn',
  timeout: 'warn',
  region_unsupported: 'error',
  configuration_incomplete: 'warn',
} as const satisfies Record<ConnectionStatus, Tone>;

export function statusTone(status: ConnectionStatus): Tone {
  return STATUS_TONE[status];
}

export function byCategory(specs: readonly ProviderSpec[]): readonly {
  readonly category: ProviderCategory;
  readonly specs: readonly ProviderSpec[];
}[] {
  return CATEGORY_ORDER.map((category) => ({
    category,
    specs: specs.filter((spec) => spec.category === category),
  })).filter((group) => group.specs.length > 0);
}

/** The fields shown for one authentication method — a field naming no method applies to all. */
export function fieldsFor(
  spec: ProviderSpec,
  method: AuthMethod,
): readonly ProviderField[] {
  return spec.fields.filter(
    (field) => field.auth_methods.length === 0 || field.auth_methods.includes(method),
  );
}

/** The non-sensitive values to submit: only fields this method shows, defaults filled. */
export function settingsFor(
  spec: ProviderSpec,
  method: AuthMethod,
  values: Readonly<Record<string, string>>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const field of fieldsFor(spec, method)) {
    if (field.sensitive) continue;
    const value = (values[field.name] ?? '').trim();
    if (value !== '') out[field.name] = value;
  }
  return out;
}

/** The sensitive values typed in this submission, and nothing else. Never the stored ones — the
 * screen never has those. An empty result on an edit means "keep the stored credential". */
export function credentialsFor(
  spec: ProviderSpec,
  method: AuthMethod,
  secrets: Readonly<Record<string, string>>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const field of fieldsFor(spec, method)) {
    if (!field.sensitive) continue;
    const value = secrets[field.name] ?? '';
    if (value.trim() !== '') out[field.name] = value;
  }
  return out;
}

/** Required fields still empty. A stored credential satisfies its sensitive fields on an edit. */
export function missingFields(
  spec: ProviderSpec,
  method: AuthMethod,
  values: Readonly<Record<string, string>>,
  secrets: Readonly<Record<string, string>>,
  stored: readonly string[],
): readonly string[] {
  const typedAnySecret = Object.keys(credentialsFor(spec, method, secrets)).length > 0;
  return fieldsFor(spec, method)
    .filter((field) => field.required)
    .filter((field) => {
      if (field.sensitive) {
        const typed = (secrets[field.name] ?? '').trim() !== '';
        /* A replacement is a whole credential: once anything is typed, every required secret is. */
        return !typed && (typedAnySecret || !stored.includes(field.name));
      }
      return (values[field.name] ?? '').trim() === '' && field.default === '';
    })
    .map((field) => field.label);
}

/** Only a model whose last real test passed may serve the tenant — the server refuses otherwise,
 * and the button says why before the round trip. */
export function canActivate(
  model: ModelConfigView,
  provider: ProviderConfigView,
): boolean {
  return model.enabled && provider.enabled && model.status === 'connected';
}

export function initialValues(
  spec: ProviderSpec,
  stored: ProviderConfigView | undefined,
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const field of spec.fields) {
    if (field.sensitive) continue;
    values[field.name] = stored?.settings[field.name] ?? field.default;
  }
  return values;
}

export function preferredMethod(spec: ProviderSpec): AuthMethod {
  /* The catalog lists the identity-based method first where one exists — workload identity before
   * a key file — so the form opens on the safer choice. */
  return spec.auth_methods[0] ?? 'none';
}

export interface ProviderChoice {
  readonly kind: ProviderKind;
  readonly category: ProviderCategory;
  readonly label: string;
  readonly isDefault: boolean;
  /** How many configurations of this kind the tenant has stored. */
  readonly configured: number;
}

/** The provider dropdown's choices: every catalog entry, in category order, with what is stored.
 *
 * **From the catalog the server serves, never a list in this file** — a provider the backend adds
 * appears here without a frontend change, and one it withdraws cannot linger as a dead option.
 */
export function providerChoices(
  specs: readonly ProviderSpec[],
  providers: readonly ProviderConfigView[],
): readonly ProviderChoice[] {
  return byCategory(specs).flatMap((group) =>
    group.specs.map((spec) => ({
      kind: spec.kind,
      category: group.category,
      label: spec.display_name,
      isDefault: spec.is_default,
      configured: providers.filter((provider) => provider.kind === spec.kind).length,
    })),
  );
}
